import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Sparkles, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { usePromptValidation } from '../hooks/usePromptValidation';
import { PromptConflict } from '../lib/llmService';

interface PromptEditorProps {
  initialValue?: string;
  className?: string;
}

export const PromptEditor: React.FC<PromptEditorProps> = ({ 
  initialValue = '', 
  className 
}) => {
  const {
    prompt,
    setPrompt,
    validation,
    isValidating,
    applyFix,
    magicEnhance,
  } = usePromptValidation(initialValue);

  const editableRef = useRef<HTMLDivElement>(null);
  const [cursorPos, setCursorPos] = useState<number | null>(null);

  // Helper to save cursor position
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editableRef.current) return;
    
    const range = selection.getRangeAt(0);
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(editableRef.current);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    setCursorPos(preSelectionRange.toString().length);
  };

  // Helper to restore cursor position
  const restoreCursorPosition = (pos: number) => {
    if (!editableRef.current) return;
    
    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    let charCount = 0;
    let nodeStack: Node[] = [editableRef.current];
    let found = false;

    while (nodeStack.length > 0 && !found) {
      const node = nodeStack.pop()!;
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCharCount = charCount + (node.textContent?.length || 0);
        if (pos <= nextCharCount) {
          range.setStart(node, pos - charCount);
          range.collapse(true);
          found = true;
        }
        charCount = nextCharCount;
      } else {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
    }

    if (found) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newText = e.currentTarget.innerText;
    saveCursorPosition();
    setPrompt(newText);
  };

  useEffect(() => {
    if (cursorPos !== null) {
      restoreCursorPosition(cursorPos);
    }
  }, [prompt]);

  const renderContent = () => {
    if (!validation || !validation.conflicts.length) {
      return prompt;
    }

    let lastIdx = 0;
    const elements: (string | JSX.Element)[] = [];
    const sortedConflicts = [...validation.conflicts].sort((a, b) => 
      prompt.indexOf(a.text_span) - prompt.indexOf(b.text_span)
    );

    sortedConflicts.forEach((conflict, idx) => {
      const startIdx = prompt.indexOf(conflict.text_span, lastIdx);
      if (startIdx === -1) return;

      // Add text before conflict
      elements.push(prompt.substring(lastIdx, startIdx));

      // Add highlighted conflict with Popover
      elements.push(
        <Popover key={`${conflict.text_span}-${idx}`}>
          <PopoverTrigger asChild>
            <span className="bg-red-100 text-red-700 border-b-2 border-red-500 cursor-pointer hover:bg-red-200 transition-colors px-0.5 rounded-sm">
              {conflict.text_span}
            </span>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-1 shrink-0" />
                <p className="text-sm font-medium">{conflict.reason}</p>
              </div>
              <Button 
                size="sm" 
                className="w-full justify-start text-left h-auto py-2"
                variant="outline"
                onClick={() => applyFix(conflict)}
              >
                <div className="flex flex-col items-start gap-0.5">
                  <span className="text-[10px] uppercase text-muted-foreground font-bold">Apply Fix</span>
                  <span className="text-sm">{conflict.suggested_fix}</span>
                </div>
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      );

      lastIdx = startIdx + conflict.text_span.length;
    });

    // Add remaining text
    elements.push(prompt.substring(lastIdx));

    return elements;
  };

  return (
    <div className={cn("flex flex-col gap-4 w-full max-w-2xl mx-auto p-6 bg-card rounded-xl border shadow-sm", className)}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Smart Prompt Editor
            {isValidating && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          </h2>
          {validation && !isValidating && (
            <div className={cn(
              "flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
              validation.is_consistent ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            )}>
              {validation.is_consistent ? (
                <>
                  <CheckCircle2 className="w-3 h-3" />
                  <span>Consistent</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-3 h-3" />
                  <span>{validation.conflicts.length} Conflict{validation.conflicts.length > 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          )}
        </div>
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={magicEnhance}
          disabled={!validation?.conflicts.length || isValidating}
          className="text-primary hover:text-primary hover:bg-primary/10 gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Magic Enhance
        </Button>
      </div>

      <div 
        ref={editableRef}
        contentEditable
        onInput={handleInput}
        suppressContentEditableWarning
        className={cn(
          "min-h-[200px] p-4 rounded-lg border-2 border-dashed transition-all focus:outline-none focus:ring-2 focus:ring-primary/20",
          isValidating ? "opacity-70 animate-pulse bg-muted/30" : "bg-background",
          !validation?.is_consistent && validation?.conflicts.length ? "border-red-200" : "border-border"
        )}
      >
        {renderContent()}
      </div>

      <div className="text-xs text-muted-foreground flex justify-between items-center">
        <span>Type your prompt above to start validation</span>
        <span>{prompt.length} characters</span>
      </div>
    </div>
  );
};
